/**
 * Products Page
 *
 * List all products with status and selection.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  ResourceList,
  ResourceItem,
  Text,
  Badge,
  Button,
  Filters,
  ChoiceList,
  EmptyState,
  Banner,
  InlineStack,
  BlockStack,
  SkeletonBodyText,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import type { Product } from '../types';

type StatusFilter = 'all' | 'pending' | 'optimized' | 'applied' | 'error';

export function ProductsPage() {
  const navigate = useNavigate();
  const api = useApi();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Produkte');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const handleSync = useCallback(async () => {
    try {
      setSyncing(true);
      const result = await api.syncProducts();
      await loadProducts();
      console.log(`Synced ${result.synced} products`);
    } catch (err) {
      setError('Fehler beim Synchronisieren');
      console.error(err);
    } finally {
      setSyncing(false);
    }
  }, [api, loadProducts]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filteredProducts = products.filter((product) => {
    if (statusFilter === 'all') return true;
    return product.status === statusFilter;
  });

  const statusBadge = (status: Product['status']) => {
    switch (status) {
      case 'pending':
        return <Badge>Ausstehend</Badge>;
      case 'optimized':
        return <Badge tone="info">Optimiert</Badge>;
      case 'applied':
        return <Badge tone="success">Angewendet</Badge>;
      case 'error':
        return <Badge tone="critical">Fehler</Badge>;
    }
  };

  const handleOptimizeSelected = () => {
    navigate('/optimize', { state: { productIds: selectedItems } });
  };

  if (loading) {
    return (
      <Page title="Produkte">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={10} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const emptyStateMarkup = (
    <EmptyState
      heading="Keine Produkte gefunden"
      action={{ content: 'Produkte synchronisieren', onAction: handleSync, loading: syncing }}
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>Synchronisiere deine Produkte von Shopify, um sie zu optimieren.</p>
    </EmptyState>
  );

  const promotedBulkActions = [
    {
      content: 'Ausgewählte optimieren',
      onAction: handleOptimizeSelected,
    },
  ];

  return (
    <Page
      title="Produkte"
      primaryAction={{
        content: 'Synchronisieren',
        onAction: handleSync,
        loading: syncing,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card padding="0">
            <ResourceList
              resourceName={{ singular: 'Produkt', plural: 'Produkte' }}
              items={filteredProducts}
              selectedItems={selectedItems}
              onSelectionChange={(items) => {
                if (Array.isArray(items)) {
                  setSelectedItems(items);
                } else if (items === 'All') {
                  setSelectedItems(filteredProducts.map(p => p.id));
                }
              }}
              promotedBulkActions={promotedBulkActions}
              selectable
              emptyState={emptyStateMarkup}
              filterControl={
                <Filters
                  filters={[
                    {
                      key: 'status',
                      label: 'Status',
                      filter: (
                        <ChoiceList
                          title="Status"
                          titleHidden
                          choices={[
                            { label: 'Alle', value: 'all' },
                            { label: 'Ausstehend', value: 'pending' },
                            { label: 'Optimiert', value: 'optimized' },
                            { label: 'Angewendet', value: 'applied' },
                            { label: 'Fehler', value: 'error' },
                          ]}
                          selected={[statusFilter]}
                          onChange={(selected) => setStatusFilter(selected[0] as StatusFilter)}
                        />
                      ),
                      shortcut: true,
                    },
                  ]}
                  onQueryChange={() => {}}
                  onQueryClear={() => {}}
                  onClearAll={() => setStatusFilter('all')}
                />
              }
              renderItem={(item) => {
                const { id, title, status, optimizedAt } = item;
                return (
                  <ResourceItem
                    id={id}
                    accessibilityLabel={`Details zu ${title}`}
                    onClick={() => navigate(`/optimize?product=${id}`)}
                  >
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack gap="100">
                        <Text as="span" variant="bodyMd" fontWeight="bold">
                          {title}
                        </Text>
                        {optimizedAt && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            Optimiert: {new Date(optimizedAt).toLocaleDateString('de-DE')}
                          </Text>
                        )}
                      </BlockStack>
                      {statusBadge(status)}
                    </InlineStack>
                  </ResourceItem>
                );
              }}
            />
          </Card>
        </Layout.Section>

        {selectedItems.length > 0 && (
          <Layout.Section>
            <InlineStack align="end">
              <Button variant="primary" onClick={handleOptimizeSelected}>
                {`${selectedItems.length} Produkte optimieren`}
              </Button>
            </InlineStack>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
